import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Button, Tooltip, Alert } from '@mui/material';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import UsbIcon from '@mui/icons-material/Usb';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScienceIcon from '@mui/icons-material/Science';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { useConnectionWizard } from '../device/ConnectionWizard';
import { VideoPlayer } from '../measurement/components/VideoPlayer';
import { useAuthStore, useUser } from '../../store';
import { isApiError } from '../../services/authService';

interface GuideStageIndicatorProps {
  currentStep: number;
  totalSteps: number;
}

/**
 * GuideStageIndicator - "Step X of Y" text + progress dots for a guide topic's stages.
 *
 * Styled after StepProgressIndicator.tsx's prop shape (currentStep/totalSteps driven by
 * local state) rather than pulling in MUI's Stepper, matching this codebase's convention.
 */
function GuideStageIndicator({ currentStep, totalSteps }: GuideStageIndicatorProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: 3, mb: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px' }}>
        Step {currentStep} of {totalSteps}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
          <Box
            key={step}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: step <= currentStep ? 'primary.main' : 'action.disabledBackground',
              transition: 'background-color 300ms ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

interface GuideStage {
  label: string;
  icon: React.ReactNode;
  body: React.ReactNode;
  video?: { src: string; poster: string; loopCount?: number };
}

const connectDeviceStages: GuideStage[] = [
  {
    label: 'Choose connection type',
    icon: (
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
        <Tooltip title="Coming soon" arrow>
          <BluetoothIcon
            aria-label="Bluetooth (coming soon)"
            tabIndex={0}
            sx={{ fontSize: 48, color: 'text.disabled' }}
          />
        </Tooltip>
        <UsbIcon sx={{ fontSize: 56, color: 'primary.main' }} />
      </Box>
    ),
    body: (
      <>
        <Typography variant="body1" gutterBottom>
          Cable is the supported connection type today — Bluetooth is coming soon.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Plug your reader into the computer with its USB cable and choose <strong>Cable</strong>. This
          triggers your browser&apos;s device picker — select your reader from the list to continue.
        </Typography>
      </>
    ),
  },
  {
    label: 'Verifying',
    icon: <MonitorHeartIcon sx={{ fontSize: 56, color: 'primary.main' }} />,
    body: (
      <>
        <Typography variant="body1" gutterBottom>
          LabCloud automatically runs a brief health check.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          After you select your reader, a short &ldquo;verifying device status&rdquo; check confirms it&apos;s
          ready — battery, temperature, and firmware are checked in the background. This only takes a
          few seconds.
        </Typography>
      </>
    ),
  },
  {
    label: 'Success',
    icon: <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main' }} />,
    body: (
      <>
        <Typography variant="body1" gutterBottom>
          A green success screen confirms your reader is connected.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          It shows your reader&apos;s serial number along with its battery level, temperature, and
          firmware version. From here, selecting <strong>Start test</strong> takes you straight into
          running your first measurement.
        </Typography>
      </>
    ),
  },
];

function ConnectYourDeviceGuide() {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = connectDeviceStages.length;
  const stage = connectDeviceStages[currentStep - 1];
  const { open: openConnectionWizard } = useConnectionWizard();

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1.5,
          px: 2,
          py: 4,
        }}
      >
        {stage.icon}
        <Typography variant="h6" component="p">{stage.label}</Typography>
        <Box sx={{ maxWidth: 480 }}>{stage.body}</Box>
        {currentStep === 1 && (
          <Button variant="contained" onClick={() => openConnectionWizard()} sx={{ mt: 1 }}>
            Connect your device now
          </Button>
        )}
      </Box>

      <GuideStageIndicator currentStep={currentStep} totalSteps={totalSteps} />

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
        <Button
          variant="text"
          disabled={currentStep === 1}
          onClick={() => setCurrentStep((step) => Math.max(1, step - 1))}
        >
          Back
        </Button>
        <Button
          variant="contained"
          disabled={currentStep === totalSteps}
          onClick={() => setCurrentStep((step) => Math.min(totalSteps, step + 1))}
        >
          Next
        </Button>
      </Box>

      {currentStep === 1 && (
      <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Device not found?
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Please make sure the device is:
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 2.5, mb: 0 }}>
          <li>
            <Typography variant="body2" color="text.secondary">Plugged in via USB cable</Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">Powered on</Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">Not connected to another computer</Typography>
          </li>
        </Box>
      </Box>
      )}
    </Box>
  );
}

const measurementStages: GuideStage[] = [
  {
    label: 'Select your panel',
    icon: <ScienceIcon sx={{ fontSize: 56, color: 'primary.main' }} />,
    body: (
      <>
        <Typography variant="body1" gutterBottom>
          Your test panel screens for the pathogens configured for it.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This is informational only — no selection is needed here. Review the panel details, then
          continue to prepare your sample.
        </Typography>
      </>
    ),
  },
  {
    label: 'Apply the solution',
    icon: null,
    video: { src: '/videos/step-2-apply-solution.mp4', poster: '/videos/step-2-poster.jpg' },
    body: (
      <Typography variant="body1">
        Add exactly three drops of solution from the white bottle into the test well.
      </Typography>
    ),
  },
  {
    label: 'Take your specimen',
    icon: null,
    video: { src: '/videos/step-3-take-specimen.mp4', poster: '/videos/step-3-poster.jpg', loopCount: 5 },
    body: (
      <>
        <Typography variant="body1" gutterBottom>
          Open the swab from its packaging and carefully insert it into one side of the nose. Rotate
          the swab 5 times.
        </Typography>
        <Typography variant="body1">Remove and repeat on the other side of the nose.</Typography>
      </>
    ),
  },
  {
    label: 'Stir the swab',
    icon: null,
    video: { src: '/videos/step-4-stir-swab.mp4', poster: '/videos/step-4-poster.jpg', loopCount: 2 },
    body: (
      <>
        <Typography variant="body1" gutterBottom>
          Remove the foil from the plastic tube and insert the swab fully into the tube so it&apos;s
          submerged in the solution.
        </Typography>
        <Typography variant="body1" gutterBottom>
          Stir the swab vigorously in the solution for 30 seconds.
        </Typography>
        <Typography variant="body1">
          While removing the swab from the tube, gently press the sides of the tube to ensure the
          swab is properly drained.
        </Typography>
      </>
    ),
  },
  {
    label: 'Apply your specimen',
    icon: null,
    video: { src: '/videos/step-5-apply-specimen.mp4', poster: '/videos/step-5-poster.jpg' },
    body: (
      <Typography variant="body1">
        Close the lid on the tube, then carefully squeeze two drops of the sample solution into the
        test&apos;s well.
      </Typography>
    ),
  },
  {
    label: 'Incubating and measuring',
    icon: <HourglassBottomIcon sx={{ fontSize: 56, color: 'primary.main' }} />,
    body: (
      <>
        <Typography variant="body1" gutterBottom>
          This step is fully automatic — no action is needed, and it takes roughly 2 minutes.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The app shows incubation progress, then measurement progress. Once finished, it takes you
          straight to a full-screen results page automatically.
        </Typography>
      </>
    ),
  },
];

function RunYourFirstMeasurementGuide() {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = measurementStages.length;
  const stage = measurementStages[currentStep - 1];
  const navigate = useNavigate();

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1.5,
          px: 2,
          py: 4,
        }}
      >
        {stage.video ? (
          <Box sx={{ width: '100%', maxWidth: 480, borderRadius: 2, overflow: 'hidden' }}>
            {/* key forces a fresh VideoPlayer instance per stage — it only reads its `poster`
                prop into state on mount, so reusing one instance across stages (this component
                renders VideoPlayer at the same JSX position for every video stage) would leave
                the poster stuck on whichever stage first mounted it. */}
            <VideoPlayer
              key={stage.video.src}
              src={stage.video.src}
              poster={stage.video.poster}
              controls
              autoPlay
              loopCount={stage.video.loopCount}
            />
          </Box>
        ) : (
          stage.icon
        )}
        <Typography variant="h6" component="p">{stage.label}</Typography>
        <Box sx={{ maxWidth: 480 }}>{stage.body}</Box>
        {currentStep === 1 && (
          <Button variant="contained" onClick={() => navigate('/measurement/new')} sx={{ mt: 1 }}>
            Start your first measurement
          </Button>
        )}
      </Box>

      <GuideStageIndicator currentStep={currentStep} totalSteps={totalSteps} />

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
        <Button
          variant="text"
          disabled={currentStep === 1}
          onClick={() => setCurrentStep((step) => Math.max(1, step - 1))}
        >
          Back
        </Button>
        <Button
          variant="contained"
          disabled={currentStep === totalSteps}
          onClick={() => setCurrentStep((step) => Math.min(totalSteps, step + 1))}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

interface GuideTopic {
  id: string;
  title: string;
  content: React.ReactNode;
}

// Small, extensible list of guide topics.
const guideTopics: GuideTopic[] = [
  {
    id: 'connect-your-device',
    title: 'Connect your device',
    content: <ConnectYourDeviceGuide />,
  },
  {
    id: 'run-your-first-measurement',
    title: 'Run your first measurement',
    content: <RunYourFirstMeasurementGuide />,
  },
];

/**
 * GuideAcknowledgment - the explicit "Acknowledge" action required before a
 * not-yet-acknowledged user's guide requirement clears. Gated on
 * `!user.guideAcknowledged` by the caller, so it renders identically whether
 * reached via the first-login auto-redirect or manual re-access.
 */
function GuideAcknowledgment() {
  const { acknowledgeGuide } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAcknowledge = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await acknowledgeGuide();
    } catch (err) {
      const message = isApiError(err)
        ? err.response.data.message
        : 'Failed to save your acknowledgment. Please try again.';
      setError(message);
      setIsSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Please confirm you&apos;ve reviewed this guide before continuing.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}
      <Button
        variant="contained"
        onClick={handleAcknowledge}
        disabled={isSubmitting}
        aria-busy={isSubmitting}
      >
        {isSubmitting ? 'Acknowledging…' : 'Acknowledge'}
      </Button>
    </Paper>
  );
}

export function GuidePage() {
  const user = useUser();

  return (
    <Box p={3}>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={600}>Quick-start guide</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Step-by-step walkthroughs for the things you need to do before producing a result.
        </Typography>
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        {guideTopics.map((topic) => (
          <Paper key={topic.id} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {topic.title}
            </Typography>
            {topic.content}
          </Paper>
        ))}

        {!user?.guideAcknowledged && <GuideAcknowledgment />}
      </Box>
    </Box>
  );
}
